import React, { useMemo, forwardRef } from "react";
import { colorConstant } from "../../styles/colorConstant/colorConstant";
import "./PosCustomButton.css";
import { CircularProgress } from "@mui/material";

/**
 *
 * @param {Object} props
 * @param {string|React.ReactNode} [props.title=""] - Button label or icon.
 * @param {string} [props.variant="black-s"] - Button style variant.
 * @param {string} [props.width] - Button width.
 * @param {string} [props.height] - Button height.
 * @param {string} [props.p] - Padding (custom CSS variable).
 * @param {string} [props.radius="6px"] - Border radius.
 * @param {boolean} [props.transparent=false] - If true, button background is transparent.
 * @param {React.ReactNode} [props.iconOnLeft] - Icon to display on the left.
 * @param {React.ReactNode} [props.iconOnRight] - Icon to display on the right.
 * @param {function} [props.onClickFunction] - Click handler for the button.
 * @param {function} [props.leftIconFunction] - Click handler for the left icon.
 * @param {function} [props.rightIconFunction] - Click handler for the right icon.
 * @param {string|number} [props.fontSize] - Font size.
 * @param {string|number} [props.fontWeight] - Font weight.
 * @param {boolean} [props.loading=false] - If true, shows a loading spinner.
 * @param {string|number} [props.loaderSize="16px"] - Size of the loading spinner.
 * @param {boolean} [props.disabled=false] - If true, disables the button.
 * @param {boolean} [props.border=false] - If true, shows a border.
 * @param {string} [props.type="button"] - Button type attribute.
 * @returns {JSX.Element}
 *
 *  * @example
 * // Basic usage
 * <PosCustomButton
 *   title="Add"
 *   variant="blue-s"
 *   onClickFunction={() => alert('Clicked!')}
 * />
 *
 * @example
 * // With using all the props
 * <PosCustomButton
 *   title="Save"
 *   variant="green-d"
 *   width={"32px"}
 *   height={"32px"}
 *   p={"12px 12px 12px 12px"}
 *   radius="6px"
 *   transparent={false}
 *   loading={true}
 *   loaderSize="16px"
 *   iconOnLeft={<SaveIcon />}
 *   iconOnRight={<ArrowForwardIcon />}
 *   onClickFunction={handleSave}
 *   leftIconFunction={handleSave}
 *   rightIconFunction={handleSave}
 *   fontSize="1.25rem"
 *   disable={false}
 *   border={true}
 *   type="submit"
 * />
 */

export const PosCustomButton = forwardRef(
  (
    {
      title = "",
      variant = "black-s",
      width,
      height,
      p,
      radius = "6px",
      transparent = false,
      iconOnLeft,
      iconOnRight,
      onClickFunction,
      leftIconFunction,
      rightIconFunction,
      fontSize,
      fontWeight,
      loading = false,
      loaderSize = "16px",
      disabled = false,
      border = false,
      type = "button",
      iconPosition = "center",
      onMouseEnter,
      onMouseLeave,
      shadow = false,
      textColor,
      customBackgroundColor = "transparant",
      customColor = "colorConstant.surfaceG3",
      customBorder = "none",
      customHover, // customHover accepts an object: { background?: string, color?: string }
    },
    ref
  ) => {
    //Creating Memoized variants of buttons
    const variants = useMemo(() => {
      const style = {
        "custom-variant": {
          background: customBackgroundColor,
          color: customColor,
          border: customBorder,
        },
        "black-g": {
          background: "transparent",
          color: colorConstant.surfaceG3,
          border: `1.5px solid ${colorConstant.surfaceG3}`,
        },
        "black-s": {
          background: "transparent",
          color: colorConstant.surfaceG4,
          border: `1.5px solid ${colorConstant.surfaceG4}`,
        },
        "black-d": {
          background: colorConstant.surfaceG5,
          color: colorConstant.surfaceWhite,
          border: `1.5px solid ${colorConstant.surfaceG5}`,
        },
        "blue-d": {
          background: colorConstant.blueB0,
          color: colorConstant.surfaceWhite,
          border: border ? `1.5px solid ${colorConstant.blueB0}` : "none",
        },
        "blue-d-transparent": {
          background: "transparent",
          color: colorConstant.blueB0,
          border: "none",
        },
        "custom-color-d-transparnt": {
          background: "transparent",
          color: textColor,
          border: "none",
        },
        "blue-s": {
          background: transparent ? "transparent" : colorConstant.blueB4,
          color: colorConstant.blueB0,
          border: `1.5px solid ${colorConstant.blueB0}`,
        },
        "blue-h": {
          background: colorConstant.blueB2,
          color: colorConstant.surfaceWhite,
          border: `1px solid ${colorConstant.blueB2}`,
        },
        "white-h": {
          background: colorConstant.surfaceG0,
          color: colorConstant.surfaceG3,
          border: border ? `1px solid ${colorConstant.surfaceG2}` : "none",
        },
        "white-d": {
          background: colorConstant.surfaceG0,
          color: colorConstant.surfaceBlack,
          border: `1px solid ${colorConstant.surfaceG1}`,
        },
        "green-d": {
          background: colorConstant.greenG4,
          color: colorConstant.greenG1,
          border: border ? `1.5px solid ${colorConstant.greenG1}` : "none",
        },
        "green-s": {
          background: "transparent",
          color: colorConstant.greenG1,
          border: `1px solid ${colorConstant.greenG1}`,
        },
        "b2b-transparent": {
          background: "transparent",
          color: colorConstant.surfaceBlack,
          border: "none",
        },
        "primary-rounded": {
          background: colorConstant.blueB0,
          color: colorConstant.surfaceWhite,
          border: `1px solid ${colorConstant.blueB0}`,
        },
        "danger-soft": {
          background: "#FFF1F2",
          color: "#C70036",
          border: "1px solid #FFA29D",
        },
        "blue-s-transparent": {
          background: "transparent",
          color: colorConstant.blueB0,
          border: "none",
        },
        "red-transparent": {
          background: "transparent",
          color: colorConstant.redSolid,
          border: `1px solid ${colorConstant.redSolid}`,
        },
        "red-d": {
          background: "red",
          color: "white",
          border: `1px solid ${colorConstant.redSolid}`,
        },
        "light-red": {
          background: "#FFF2F1",
          color: "#ED2015",
          border: "1px solid #FFA29D",
        },
        "red-sand": {
          background: colorConstant?.surfaceWhite,
          color: "#F54900",
          border: "1px solid #FEC381",
        },
        "selected-button": {
          background: "white",
          color: colorConstant.greenG1,
          border: `1px solid ${colorConstant.transparent}`,
        },
        "dark-mode-one": {
          background: "var(--dark-background)",
          border: `1px solid var(--dark-background)`,
        },
        "black-no-border": {
          background: "transparent",
          color: colorConstant.surfaceBlack,
          border: "none",
        },
        "yellow-no-border": {
          background: "transparent",
          color: colorConstant.sandSolid,
          border: "none",
        },
        "yellow-s": {
          background: "transparent",
          color: colorConstant.sandSolid,
          border: `1px solid ${colorConstant?.sandSolid}`,
        },
        "profile-back": {
          background: "none",
          color: "#64748b",
          border: "none",
        },
        "login-primary": {
          background: "#f1f5f9",
          color: "#94a3b8",
          border: "1px solid #e2e8f0",
        },

        "login-active": {
          background: "#2a71ff",
          color: "#fff",
          border: "none",
        },
        "link-primary": {
          background: "transparent",
          color: "#2A71FF",
          border: "none",
        },
      };
      return style[variant];
    }, [variant]);

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        className={`customPosButton ${variant} ${customHover ? "has-custom-hover" : ""}`}
        onClick={onClickFunction}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{
          ...variants,
          width: width,
          height: height,
          borderRadius: radius,
          fontSize: fontSize,
          ...(p && { "--padding": p }),
          ...(shadow && {
            boxShadow: typeof shadow === "string" ? shadow : "0 2px 6px rgba(0,0,0,0.15)",
          }),
          ...(customHover?.background && { "--custom-hover-bg": customHover.background }),
          ...(customHover?.color && { "--custom-hover-color": customHover.color }),
        }}
      >
        <div
          className={`customPosButtonContainer ${iconPosition === "corner" ? "corner-layout" : ""}`}
        >
          {iconOnLeft && !loading && (
            <div
              className="customPosButtonLeftIcon"
              onClick={disabled ? undefined : leftIconFunction}
            >
              {iconOnLeft}
            </div>
          )}
          <span className="customPosButtonText" style={{ fontWeight: fontWeight }}>
            {loading ? (
              <CircularProgress size={loaderSize} sx={{ color: variants.color, display: "flex" }} />
            ) : (
              title
            )}
          </span>
          {iconOnRight && !loading && (
            <div
              className="customPosButtonRightIcon"
              onClick={disabled ? undefined : rightIconFunction}
            >
              {iconOnRight}
            </div>
          )}
        </div>
      </button>
    );
  }
);
